import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { THEME, getStatusColor, getStatusLabel, STATUS_OPTIONS } from '../../utils/theme';
import api from '../../utils/api';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';

const GameCard = ({ game, currentUser, squadMembers = [], onUpdate, onAddSession, onShowDetail, viewMode, isSelected, onToggleSelect }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editRating, setEditRating] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showQuickRate, setShowQuickRate] = useState(false);
  const [quickRateSaving, setQuickRateSaving] = useState(false);

  // Calculate compatibility score locally - dynamic based on user_data
  const calculateCompatibility = () => {
    let score = 0;
    const breakdown = [];
    const userData = game.user_data || {};
    const userEntries = Object.entries(userData);

    // Fallback to legacy fields if no user_data
    if (userEntries.length === 0) {
      const omegaHours = Math.round((game.playtime_omega || 0) / 60);
      const supeHours = Math.round((game.playtime_supe || 0) / 60);
      if (game.favorite_omega && game.favorite_supe) { score += 20; breakdown.push('Both favorited'); }
      if (omegaHours >= 5 && supeHours >= 5) { score += 15; breakdown.push('Both played 5+ hours'); }
      if (game.omega_rating >= 8 && game.supe_rating >= 8) { score += 20; breakdown.push('Both rated highly'); }
      if (game.omega_rating && game.supe_rating && Math.abs(game.omega_rating - game.supe_rating) <= 1) { score += 10; breakdown.push('Similar ratings'); }
      if (game.owned_by_omega && game.owned_by_supe) { score += 10; breakdown.push('Both own it'); }
    } else {
      // Dynamic user-based calculations
      const favorites = userEntries.filter(([, d]) => d.favorite);
      const playedHours = userEntries.map(([, d]) => Math.round((d.playtime || 0) / 60));
      const ratings = userEntries.map(([, d]) => d.rating).filter(r => r != null);
      const owned = userEntries.filter(([, d]) => d.owned);

      // All users favorited (+20)
      if (favorites.length >= 2 && favorites.length === userEntries.length) {
        score += 20; breakdown.push('All favorited');
      } else if (favorites.length >= 2) {
        score += 10; breakdown.push('Multiple favorites');
      }

      // All users played 5+ hours (+15)
      if (playedHours.length >= 2 && playedHours.every(h => h >= 5)) {
        score += 15; breakdown.push('All played 5+ hours');
      }

      // All rated highly (8+) (+20)
      if (ratings.length >= 2 && ratings.every(r => r >= 8)) {
        score += 20; breakdown.push('All rated highly');
      }

      // Ratings within 1 point of each other (+10)
      if (ratings.length >= 2) {
        const maxDiff = Math.max(...ratings) - Math.min(...ratings);
        if (maxDiff <= 1) { score += 10; breakdown.push('Similar ratings'); }
      }

      // All own it (+10)
      if (owned.length >= 2 && owned.length === userEntries.length) {
        score += 10; breakdown.push('All own it');
      }
    }

    // Online multiplayer (+5)
    if (game.is_online_multiplayer) {
      score += 5;
      breakdown.push('Online co-op');
    }

    // Good metacritic (+5)
    if (game.metacritic_score >= 75) {
      score += 5;
      breakdown.push('Highly rated');
    }

    return { score: Math.min(score, 100), breakdown };
  };

  const compatibility = calculateCompatibility();
  const compatScore = compatibility.score;
  const compatColor = compatScore >= 70 ? THEME.success : compatScore >= 40 ? THEME.warning : THEME.textMuted;

  // Use dynamic user_data from API when available, fallback to legacy fields
  const myUserData = game.user_data?.[currentUser?.username] || {};
  const myRating = myUserData.rating ?? (currentUser?.username === 'omega' ? game.omega_rating : game.supe_rating);
  const myNotes = myUserData.notes ?? (currentUser?.username === 'omega' ? game.omega_notes : game.supe_notes);
  const myColor = currentUser?.theme_color || THEME.success;
  const myFavorite = myUserData.favorite ?? (currentUser?.username === 'omega' ? game.favorite_omega : game.favorite_supe);
  const myStatus = myUserData.status ?? (currentUser?.username === 'omega' ? game.status_omega : game.status_supe);

  // Find "other" users from user_data (only squad members, not all users with game data)
  const squadMemberUsernames = new Set(squadMembers.map(m => m.username));
  const otherUsers = Object.entries(game.user_data || {})
    .filter(([username]) => username !== currentUser?.username && squadMemberUsernames.has(username));
  const otherUserData = otherUsers[0]?.[1] || {};
  const otherFavorite = otherUserData.favorite ?? (currentUser?.username === 'omega' ? game.favorite_supe : game.favorite_omega);
  const otherStatus = otherUserData.status ?? (currentUser?.username === 'omega' ? game.status_supe : game.status_omega);
  const otherUser = otherUserData.display_name || (currentUser?.username === 'omega' ? 'Supe' : 'Omega');
  const otherColor = otherUserData.color || (currentUser?.username === 'omega' ? THEME.supe : THEME.omega);
  const theyRecommended = otherUserData.recommended ?? (currentUser?.username === 'omega' ? game.recommended_by_supe : game.recommended_by_omega);

  const handleStartEdit = () => { setEditRating(myRating); setEditNotes(myNotes || ''); setIsEditing(true); };
  const handleSaveRating = async () => {
    setSaving(true);
    try { await api.put(`/api/games/${game.id}/rating`, { rating: editRating, notes: editNotes }); await onUpdate(); setIsEditing(false); }
    catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };
  const handleStatusChange = async (newStatus) => {
    try { await api.put(`/api/games/${game.id}/status`, { status: newStatus }); await onUpdate(); }
    catch (err) { alert('Failed: ' + err.message); }
  };
  const handleToggleFavorite = async () => {
    try { await api.put(`/api/games/${game.id}/favorite`); await onUpdate(); }
    catch (err) { alert('Failed: ' + err.message); }
  };
  const handleQuickRate = async (rating) => {
    setQuickRateSaving(true);
    try {
      await api.put(`/api/games/${game.id}/rating`, { rating, notes: myNotes || '' });
      await onUpdate();
      setShowQuickRate(false);
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setQuickRateSaving(false); }
  };

  // Quick Rate Stars Component (inline)
  const QuickRateStars = ({ size = 14 }) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
        <button
          key={n}
          onClick={(e) => { e.stopPropagation(); handleQuickRate(n); }}
          disabled={quickRateSaving}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: quickRateSaving ? 'not-allowed' : 'pointer',
            padding: '0 1px',
            fontSize: `${size}px`,
            lineHeight: 1,
            color: n <= (myRating || 0) ? '#fbbf24' : THEME.textDim,
            transition: 'transform 0.1s, color 0.1s',
            opacity: quickRateSaving ? 0.5 : 1
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          title={`Rate ${n}/10`}
        >
          {n <= (myRating || 0) ? '★' : '☆'}
        </button>
      ))}
      {myRating && (
        <button
          onClick={(e) => { e.stopPropagation(); handleQuickRate(null); }}
          disabled={quickRateSaving}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '0 2px',
            fontSize: `${size - 2}px`,
            color: THEME.danger,
            marginLeft: '4px'
          }}
          title="Clear rating"
        >
          ✕
        </button>
      )}
    </div>
  );

  const formatPlaytime = (minutes) => { if (!minutes) return '0h'; const hours = Math.round(minutes / 60); return hours < 1 ? '<1h' : `${hours}h`; };

  // List view
  if (viewMode === 'list') {
    return (
      <div style={{
        display: 'grid', gridTemplateColumns: '32px 28px 52px 1fr 90px 70px 45px 70px 130px 90px', gap: '12px', alignItems: 'center',
        padding: '10px 16px', background: isSelected ? `${myColor}10` : THEME.bgCard, borderRadius: '10px',
        border: `1px solid ${isSelected ? myColor : THEME.border}`, transition: 'all 0.15s'
      }}>
        <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(game.id)} style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: myColor }} />
        <button onClick={handleToggleFavorite} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: '18px', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
          <span style={{ color: myFavorite ? '#fbbf24' : THEME.textDim }}>{myFavorite ? '★' : '☆'}</span>
          {otherFavorite && <span style={{ color: otherColor, fontSize: '12px', marginLeft: '-4px', marginTop: '-8px' }}>★</span>}
        </button>
        <img src={game.header_image} alt="" style={{ width: '52px', height: '30px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer' }} onClick={() => onShowDetail(game)} onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 30"><rect fill="%231a1a24" width="52" height="30"/><text x="26" y="18" text-anchor="middle" fill="%234b5563" font-size="10">?</text></svg>'; }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
          <a href={game.steam_url} target="_blank" rel="noopener noreferrer" style={{ color: THEME.text, textDecoration: 'none', fontWeight: '500', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.name}</a>
          {game.controller_support && <span title={game.controller_support === 'full' ? 'Full Controller Support' : 'Partial Controller Support'} style={{ fontSize: '12px', opacity: game.controller_support === 'full' ? 1 : 0.7 }}>🎮</span>}
        </div>
        <span style={{ color: THEME.textMuted, fontSize: '11px' }}>{game.multiplayer_type?.split(',')[0] || 'MP'}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '13px' }}>
          {game.user_data ? Object.entries(game.user_data).slice(0, 2).map(([username, userData], idx) => (
            <React.Fragment key={username}>
              {idx > 0 && <span style={{ color: THEME.textDim }}>/</span>}
              <span style={{ color: userData.color || THEME.success, fontWeight: '600' }}>{userData.rating ?? '—'}</span>
            </React.Fragment>
          )) : (
            <>
              <span style={{ color: THEME.omega, fontWeight: '600' }}>{game.omega_rating ?? '—'}</span>
              <span style={{ color: THEME.textDim }}>/</span>
              <span style={{ color: THEME.supe, fontWeight: '600' }}>{game.supe_rating ?? '—'}</span>
            </>
          )}
        </div>
        <span style={{ color: compatColor, fontSize: '11px', fontWeight: '600' }} title={compatibility.breakdown.join(', ') || 'No compatibility factors'}>{compatScore > 0 ? `${compatScore}%` : '—'}</span>
        <span style={{ color: THEME.textMuted, fontSize: '12px' }}>{formatPlaytime(game.user_data ? Object.values(game.user_data).reduce((sum, u) => sum + (u.playtime || 0), 0) : (game.playtime_omega || 0) + (game.playtime_supe || 0))}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <select value={myStatus} onChange={e => handleStatusChange(e.target.value)} style={{ background: THEME.bg, color: getStatusColor(myStatus), border: `1px solid ${THEME.border}`, borderRadius: '6px', padding: '4px 6px', fontSize: '10px', cursor: 'pointer', fontWeight: '600' }}>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <span style={{ background: otherColor, color: '#fff', borderRadius: '6px', padding: '4px 6px', fontSize: '10px', fontWeight: '600' }}>{getStatusLabel(otherStatus)}</span>
        </div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {showQuickRate ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <QuickRateStars size={12} />
              <button onClick={() => setShowQuickRate(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: THEME.textDim, fontSize: '10px', padding: '2px' }}>✕</button>
            </div>
          ) : (
            <button onClick={() => setShowQuickRate(true)} style={{ background: `${myColor}15`, color: myColor, border: 'none', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
              ★ {myRating ?? '—'}
            </button>
          )}
        </div>
        {isEditing && <RatingModal game={game} myColor={myColor} editRating={editRating} setEditRating={setEditRating} editNotes={editNotes} setEditNotes={setEditNotes} saving={saving} onSave={handleSaveRating} onClose={() => setIsEditing(false)} />}
      </div>
    );
  }

  // Compact view
  if (viewMode === 'compact') {
    return (
      <div style={{
        background: THEME.bgCard, borderRadius: '10px', overflow: 'hidden', border: `1px solid ${isSelected ? myColor : THEME.border}`,
        transition: 'all 0.2s', transform: isHovered ? 'translateY(-2px)' : 'none', boxShadow: isHovered ? `0 8px 25px ${THEME.shadow}` : 'none'
      }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => onShowDetail(game)}>
          <img src={game.header_image} alt={game.name} style={{ width: '100%', height: '80px', objectFit: 'cover' }} onError={e => { e.target.style.background = THEME.bgElevated; }} />
          <div style={{ position: 'absolute', top: '6px', left: '6px', display: 'flex', gap: '4px' }}>
            <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(game.id)} onClick={e => e.stopPropagation()} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: myColor }} />
            <button onClick={e => { e.stopPropagation(); handleToggleFavorite(); }} style={{ background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '4px', padding: '2px 4px', cursor: 'pointer', fontSize: '12px' }}>
              <span style={{ color: myFavorite ? '#fbbf24' : THEME.textDim }}>{myFavorite ? '★' : '☆'}</span>
            </button>
          </div>
          <div style={{ position: 'absolute', top: '6px', right: '6px' }}>
            <select value={myStatus} onChange={e => { e.stopPropagation(); handleStatusChange(e.target.value); }} onClick={e => e.stopPropagation()} style={{ background: 'rgba(0,0,0,0.85)', color: getStatusColor(myStatus), border: 'none', borderRadius: '4px', padding: '3px 6px', fontSize: '10px', cursor: 'pointer', fontWeight: '600' }}>
              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {compatScore > 0 && (
            <div style={{ position: 'absolute', bottom: '4px', right: '4px', background: `${compatColor}e0`, padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: '700', color: '#000' }} title={compatibility.breakdown.join(', ')}>
              {compatScore}%
            </div>
          )}
        </div>
        <div style={{ padding: '10px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: THEME.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.name}</span>
            {game.controller_support && <span style={{ fontSize: '10px', flexShrink: 0, opacity: game.controller_support === 'full' ? 1 : 0.7 }}>🎮</span>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {showQuickRate ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <QuickRateStars size={10} />
                <button onClick={(e) => { e.stopPropagation(); setShowQuickRate(false); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: THEME.textDim, fontSize: '10px', padding: '2px' }}>✕</button>
              </div>
            ) : (
              <button onClick={(e) => { e.stopPropagation(); setShowQuickRate(true); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', gap: '6px', fontSize: '12px' }}>
                {game.user_data ? Object.entries(game.user_data).slice(0, 2).map(([username, userData], idx) => (
                  <React.Fragment key={username}>
                    {idx > 0 && <span style={{ color: THEME.textDim }}>/</span>}
                    <span style={{ color: userData.color || THEME.success, fontWeight: '600' }}>{userData.rating ?? '—'}</span>
                  </React.Fragment>
                )) : (
                  <>
                    <span style={{ color: THEME.omega, fontWeight: '600' }}>{game.omega_rating ?? '—'}</span>
                    <span style={{ color: THEME.textDim }}>/</span>
                    <span style={{ color: THEME.supe, fontWeight: '600' }}>{game.supe_rating ?? '—'}</span>
                  </>
                )}
              </button>
            )}
            <span style={{ color: THEME.textMuted, fontSize: '10px' }}>{formatPlaytime(game.user_data ? Object.values(game.user_data).reduce((sum, u) => sum + (u.playtime || 0), 0) : (game.playtime_omega || 0) + (game.playtime_supe || 0))}</span>
          </div>
        </div>
        {isEditing && <RatingModal game={game} myColor={myColor} editRating={editRating} setEditRating={setEditRating} editNotes={editNotes} setEditNotes={setEditNotes} saving={saving} onSave={handleSaveRating} onClose={() => setIsEditing(false)} />}
      </div>
    );
  }

  // Full card view - 3.0 Modern Glassmorphism Design
  return (
    <motion.div
      className="card-shine"
      whileHover={{ y: -6, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{
        background: `linear-gradient(145deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`,
        borderRadius: '18px',
        overflow: 'hidden',
        border: `1px solid ${isSelected ? myColor : THEME.glassBorder}`,
        boxShadow: isHovered ? THEME.cardGlowHover : THEME.cardGlow,
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Section */}
      <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => onShowDetail(game)}>
        <img src={game.header_image} alt={game.name} style={{ width: '100%', height: '130px', objectFit: 'cover', transition: 'transform 0.4s ease' }} onError={e => { e.target.style.background = THEME.bgElevated; }} />
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(10,10,15,0.2) 0%, rgba(10,10,15,0.6) 60%, rgba(10,10,15,0.95) 100%)',
          transition: 'opacity 0.3s ease'
        }} />

        {/* Top Controls - Glassmorphism */}
        <div style={{ position: 'absolute', top: '10px', left: '10px', right: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(game.id)} onClick={e => e.stopPropagation()} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: myColor }} />
            <button onClick={e => { e.stopPropagation(); handleToggleFavorite(); }} className="btn-modern" style={{
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              padding: '6px 8px',
              cursor: 'pointer',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '2px'
            }}>
              <span style={{ color: myFavorite ? '#fbbf24' : 'rgba(255,255,255,0.4)', transition: 'color 0.2s' }}>{myFavorite ? '★' : '☆'}</span>
              {otherFavorite && <span style={{ color: otherColor, fontSize: '11px' }}>★</span>}
            </button>
          </div>
          <select value={myStatus} onChange={e => { e.stopPropagation(); handleStatusChange(e.target.value); }} onClick={e => e.stopPropagation()} style={{
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            color: getStatusColor(myStatus),
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '10px',
            padding: '6px 12px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {/* Title on Image - Enhanced */}
        <div style={{ position: 'absolute', bottom: '12px', left: '14px', right: '14px' }}>
          <h3 style={{
            margin: 0,
            color: '#fff',
            fontSize: '15px',
            fontWeight: '700',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textShadow: '0 2px 8px rgba(0,0,0,0.8)',
            letterSpacing: '-0.01em'
          }}>{game.name}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
            <span style={{
              color: 'rgba(255,255,255,0.65)',
              fontSize: '11px',
              fontWeight: '500'
            }}>{game.multiplayer_type?.split(',')[0] || 'Co-op'}</span>
            {game.metacritic_score && <span style={{
              background: `linear-gradient(135deg, ${game.metacritic_score >= 75 ? THEME.success : game.metacritic_score >= 50 ? THEME.warning : THEME.danger}, ${game.metacritic_score >= 75 ? '#059669' : game.metacritic_score >= 50 ? '#d97706' : '#dc2626'})`,
              color: '#000',
              padding: '3px 8px',
              borderRadius: '6px',
              fontSize: '10px',
              fontWeight: '700',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}>{game.metacritic_score}</span>}
            {game.controller_support && <span style={{ fontSize: '12px', opacity: game.controller_support === 'full' ? 1 : 0.6 }}>🎮</span>}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div style={{ padding: '14px' }}>
        {/* Squad Rating Display - Supports 2-4 members */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
          {/* My Rating - Interactive */}
          <motion.div
            onClick={() => setShowQuickRate(!showQuickRate)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              flex: otherUsers.length > 1 ? '1 1 calc(50% - 5px)' : '1',
              minWidth: otherUsers.length > 1 ? '85px' : 'auto',
              background: `linear-gradient(135deg, ${myColor}15 0%, ${myColor}08 100%)`,
              borderRadius: '12px',
              padding: '12px',
              cursor: 'pointer',
              border: `1px solid ${showQuickRate ? myColor : `${myColor}25`}`,
              transition: 'all 0.2s ease',
              boxShadow: showQuickRate ? `0 0 20px ${myColor}30` : 'none'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: myColor, boxShadow: `0 0 8px ${myColor}60` }} />
              <span style={{ color: myColor, fontSize: '11px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.display_name || 'You'}</span>
            </div>
            {showQuickRate ? (
              <QuickRateStars size={12} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                <span style={{ fontSize: otherUsers.length > 1 ? '20px' : '24px', fontWeight: '800', color: myColor, letterSpacing: '-0.02em' }}>{myRating ?? '—'}</span>
                <span style={{ fontSize: '12px', color: THEME.textMuted, fontWeight: '500' }}>/10</span>
              </div>
            )}
          </motion.div>
          {/* Other Squad Members' Ratings - Show ALL members */}
          {otherUsers.map(([username, userData]) => (
            <div key={username} style={{
              flex: otherUsers.length > 1 ? '1 1 calc(50% - 5px)' : '1',
              minWidth: otherUsers.length > 1 ? '85px' : 'auto',
              background: `linear-gradient(135deg, ${userData.color || THEME.info}15 0%, ${userData.color || THEME.info}08 100%)`,
              borderRadius: '12px',
              padding: '12px',
              border: `1px solid ${userData.color || THEME.info}25`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: userData.color || THEME.info, boxShadow: `0 0 8px ${userData.color || THEME.info}60` }} />
                <span style={{ color: userData.color || THEME.info, fontSize: '11px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userData.display_name || username}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                <span style={{ fontSize: otherUsers.length > 1 ? '20px' : '24px', fontWeight: '800', color: userData.color || THEME.info, letterSpacing: '-0.02em' }}>{userData.rating ?? '—'}</span>
                <span style={{ fontSize: '12px', color: THEME.textMuted, fontWeight: '500' }}>/10</span>
              </div>
            </div>
          ))}
        </div>

        {/* Tags - Modern Pill Style */}
        {game.tags?.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {(() => {
              const tagsByName = {};
              game.tags.forEach(t => { if (!tagsByName[t.name]) tagsByName[t.name] = []; tagsByName[t.name].push(t); });
              return Object.entries(tagsByName).slice(0, 2).map(([name, tags]) => {
                const isShared = tags.length > 1;
                const color = isShared ? THEME.accent : tags[0].color;
                return <span key={name} style={{
                  background: `linear-gradient(135deg, ${color}25 0%, ${color}15 100%)`,
                  color,
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontSize: '10px',
                  fontWeight: '600',
                  border: `1px solid ${color}30`,
                  letterSpacing: '0.02em'
                }}>#{name}</span>;
              });
            })()}
          </div>
        )}

        {/* Info Row - Enhanced */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 0 2px',
          borderTop: `1px solid ${THEME.glassBorder}`,
          marginTop: '4px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ color: THEME.textMuted, fontSize: '11px', fontWeight: '500' }}>
              {formatPlaytime((myUserData.playtime ?? 0) + otherUsers.reduce((sum, [, ud]) => sum + (ud.playtime ?? 0), 0))}
            </span>
            {compatScore >= 50 && (
              <span style={{
                background: `${compatColor}20`,
                color: compatColor,
                padding: '3px 8px',
                borderRadius: '6px',
                fontSize: '10px',
                fontWeight: '700'
              }} title={compatibility.breakdown.join(' • ')}>🤝 {compatScore}%</span>
            )}
            {otherUsers.filter(([, ud]) => ud.status && ud.status !== 'unplayed' && ud.status !== myStatus).slice(0, 2).map(([username, ud]) => (
              <span key={username} style={{
                background: `${ud.color || THEME.info}20`,
                color: ud.color || THEME.info,
                padding: '3px 8px',
                borderRadius: '6px',
                fontSize: '9px',
                fontWeight: '600'
              }}>{getStatusLabel(ud.status)}</span>
            ))}
          </div>
          <motion.button
            onClick={() => onAddSession(game)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              background: `linear-gradient(135deg, ${THEME.accent} 0%, ${THEME.accent}dd 100%)`,
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: `0 4px 12px ${THEME.accent}40`
            }}
          >+ Log</motion.button>
        </div>
      </div>

      {isEditing && <RatingModal game={game} myColor={myColor} editRating={editRating} setEditRating={setEditRating} editNotes={editNotes} setEditNotes={setEditNotes} saving={saving} onSave={handleSaveRating} onClose={() => setIsEditing(false)} />}
    </motion.div>
  );
};

export default GameCard;
